import { Injectable } from '@angular/core';
import { TriggerbeeApi, TriggerbeeRequester } from '../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from '../core/logger.service';
import {
  Automation,
  AutomationAction,
  AutomationErrorType,
  AutomationStatusResponse,
  CrossSiteCopyAutomationResponse,
} from './automations-models';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { Observable } from 'rxjs';
import { OnsiteCampaign } from 'app/campaign/campaign.model';
import WidgetAutomationHelpers from 'app/campaign/widget-automation-helpers';
import { RequestResult } from 'app/core/triggerbee/request-result';

@Injectable()
export class AutomationsService {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('AutomationsService');
  }

  public async getAutomation(id: string): Promise<Automation> {
    const response = await this.requester.getAsync<Automation>(TriggerbeeApi.Gateway, 'automations/' + id);
    let result = WidgetAutomationHelpers.backwardCompatibilityTagIdProp(response.data);
    return result || new Automation();
  }

  public async getAutomations(): Promise<Automation[]> {
    const response = await this.requester.getAsync<Automation[]>(TriggerbeeApi.Gateway, 'automations');
    return response.data || [];
  }

  public getAutomationActions(): Observable<AutomationAction[]> {
    return this.http.get(`${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/automations/actions`);
  }

  public deleteAutomation(id: string): Observable<void> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.delete(`${baseUrl}/automations/${id}`);
  }

  public async saveAutomation(automation: Automation): Promise<Automation> {
    const response = await this.requester.postAsync<Automation>(TriggerbeeApi.Gateway, 'automations', automation);
    if (!response.isPositive()) {
      this.logger.error(`save failed : ${response.toString()}`);
      return null;
    }
    return response.data;
  }

  public async updateAutomation(automation: Automation): Promise<Automation> {
    const response = await this.requester.putAsync<Automation>(
      TriggerbeeApi.Gateway,
      'automations/' + automation.id,
      automation,
    );
    if (!response.isPositive()) {
      this.logger.error(`save failed : ${response.toString()}`);
      return null;
    }
    return response.data;
  }

  public duplicateAutomation(id: string): Promise<RequestResult<Automation>> {
    return this.requester.postAsync<Automation>(TriggerbeeApi.Gateway, `automations/${id}/copy`, {});
  }

  public copyAutomationToAccount(
    id: string,
    targetSiteId: number,
  ): Promise<RequestResult<CrossSiteCopyAutomationResponse>> {
    return this.requester.postAsync<CrossSiteCopyAutomationResponse>(
      TriggerbeeApi.Gateway,
      `automations/${id}/copy-cross-site`,
      { targetSiteId },
    );
  }

  public async getAutomationStatuses(recentRuns?: number): Promise<AutomationStatusResponse[]> {
    const baseUrl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    const query = recentRuns ? `?recentRuns=${recentRuns}` : '';
    return this.http.get(`${baseUrl}/automations/statuses${query}`).toPromise();
  }

  public getErrorTypeAsString(automationErrorType?: AutomationErrorType): string {
    if (automationErrorType === null) return null;
    return AutomationErrorType[automationErrorType];
  }

  public async getCampaignsConnectedToAutomation(automationId: string): Promise<OnsiteCampaign[]> {
    const baseurl = this.http.getBaseUrl(TriggerbeeApi.Gateway);
    return this.http.get(`${baseurl}/onsite-campaigns?automationId=${automationId}`).toPromise();
  }
}
